import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { MagnifyingGlass, CurrencyInr } from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import { PaymentCell, RecordPaymentDialog } from "../../components/payments/PaymentInfo";
import { ExportPaymentsButton } from "../../components/payments/ExportPayments";

const TYPES = ["all", "doctor", "lab", "ambulance"];
const PAY = [{ k: "", l: "All payments" }, { k: "paid", l: "Paid" }, { k: "unpaid", l: "Unpaid" }];
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—");

function BookingRow({ b, onPay }) {
  const d = b.details || {};
  return (
    <tr data-testid={`hb-row-${b.no}`} className="hover:bg-[#FAFAF7]">
      <td className="px-4 py-3 font-mono text-[12px]">{b.no}</td>
      <td className="px-4 py-3">
        <div className="font-medium">{d.patient_name || "—"}</div>
        <div className="text-[11px] text-[#8A8780]">{b.customer_phone}</div>
      </td>
      <td className="px-4 py-3 capitalize">{b.type}<div className="text-[11px] text-[#8A8780] normal-case">{b.partner_name}</div></td>
      <td className="px-4 py-3 text-[12px] capitalize">{b.status}</td>
      <td className="px-4 py-3"><PaymentCell payment={b.payment_summary} testid={`hb-payment-${b.id}`}/></td>
      <td className="px-4 py-3 text-[11px] text-[#8A8780]">{fmtDate(b.created_at)}</td>
      <td className="px-4 py-3 text-right">
        {b.payment_summary?.state === "unpaid" && b.status !== "cancelled" && (
          <button onClick={() => onPay(b)} data-testid={`hb-record-pay-${b.id}`}
                  className="inline-flex items-center gap-1 text-xs font-medium text-[#B91C1C] hover:underline">
            <CurrencyInr size={12}/>Record payment
          </button>
        )}
      </td>
    </tr>
  );
}

export function HealthBookingsTab() {
  const [rows, setRows] = useState([]);
  const [type, setType] = useState("all");
  const [pay, setPay] = useState("");
  const [q, setQ] = useState("");
  const [payFor, setPayFor] = useState(null);

  const load = async () => {
    try {
      const p = new URLSearchParams({ type });
      if (pay) p.set("payment", pay);
      if (q) p.set("q", q);
      const r = await api.get(`/health-services/bookings?${p}`);
      setRows(r.data.bookings || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [type, pay]);

  return (
    <div className="space-y-4" data-testid="hb-tab">
      <div className="flex items-center gap-2 flex-wrap">
        {TYPES.map(t => (
          <button key={t} onClick={() => setType(t)} data-testid={`hb-type-${t}`}
                  className={`px-3 py-1.5 text-sm rounded-md capitalize ${type === t ? "bg-[#B91C1C] text-white" : "bg-white border border-[#E5E2D9] text-[#5C5A55]"}`}>
            {t}
          </button>
        ))}
        <select value={pay} onChange={e => setPay(e.target.value)} data-testid="hb-filter-payment"
                className="h-9 px-3 border border-[#E5E2D9] rounded-md bg-white text-sm">
          {PAY.map(o => <option key={o.k} value={o.k}>{o.l}</option>)}
        </select>
        <div className="flex-1"/>
        <ExportPaymentsButton url="/health-services/bookings/export.csv" testid="hb-export"/>
        <div className="relative">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A8780]"/>
          <input value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => e.key === "Enter" && load()}
                 placeholder="Search booking, phone, patient…" data-testid="hb-search"
                 className="pl-9 pr-3 py-1.5 text-sm rounded-md border border-[#E5E2D9]"/>
        </div>
      </div>
      <div className="bg-white rounded-lg border border-[#E5E2D9] overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#F5F3EE] text-[#5C5A55] text-[11px] uppercase tracking-wider">
            <tr>
              {["Booking#", "Patient", "Type / Provider", "Status", "Payment", "Created", ""].map(h =>
                <th key={h} className="text-left px-4 py-3">{h}</th>)}
            </tr>
          </thead>
          <tbody className="divide-y divide-[#F0EDE4]">
            {rows.length === 0 && <tr><td colSpan={7} className="text-center py-10 text-[#8A8780]">No health bookings</td></tr>}
            {rows.map(b => <BookingRow key={b.id} b={b} onPay={setPayFor}/>)}
          </tbody>
        </table>
      </div>
      {payFor && (
        <RecordPaymentDialog url={`/health-services/bookings/${payFor.id}/payment`} title={payFor.no}
                             defaultAmount={payFor.amount} onClose={() => setPayFor(null)}
                             onSaved={() => { setPayFor(null); load(); }}/>
      )}
    </div>
  );
}
